import { Component, HostListener, NgZone, OnDestroy, OnInit } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { Device } from '@capacitor/device';
import { MenuController, PopoverController } from '@ionic/angular';
import { Subscription } from 'rxjs';
import { filter, tap } from 'rxjs/operators';
import { BrowserService } from 'src/app/services/native/browser.service';
import { NavigationService } from 'src/app/services/navigation.service';
import { IUserData } from 'src/app/services/user/user-data.interface';
import { UserDataService } from 'src/app/services/user/user-data.service';
import { environment } from 'src/environments/environment';
import { Country } from './interfaces/country.interface';
import { AuthService } from './services/auth.service';
import { CurrentCountryService } from './services/current-country.service';
import { MenuItem, MenuService } from './services/menu.service';

@Component({
  selector: 'app-menu',
  templateUrl: './menu.component.html',
  styleUrls: ['./menu.component.scss'],
})
export class MenuComponent implements OnInit, OnDestroy {
  isWebPlatform = false;
  appVersion = environment.version;
  menuItems: MenuItem[] = [];
  country: Country;
  userData: IUserData;
  isSubscriber = false;
  loadingMenu = true;
  publicRoutesPrefixes = ['/login', '/register', '/recover', '/'];
  hideMenu = false;
  mobile = false;
  menuVisible = true;
  osInfo: any;
  screenWidth: number;
  openPopoverId: string | null = null;
  loggedIn = false;
  private subscription = new Subscription();

  private readonly rutasSinMenu = [
    '',
    '/ar/contact/finish',
    '/recover',
    '/ar/check-account',
    '/login',
    '/register/form',
    '/ar/discount/finish',
    '/ar/servicios/ingresos-brutos/register/finish-congratulation',
    '/ar/servicios/monotributo/modificacion/finish-congratulation',
    '/no-internet',
    '/mantenimiento',
  ];

  constructor(
    private menuCtrl: MenuController,
    private popoverCtrl: PopoverController,
    private authService: AuthService,
    private currentCountryService: CurrentCountryService,
    private navigationService: NavigationService,
    private browserService: BrowserService,
    private ngZone: NgZone,
    private userDataService: UserDataService,
    private router: Router,
    private menuService: MenuService
  ) {}

  async ngOnInit(): Promise<void> {
    this.subscription.add(
      this.authService.isLoggedIn
        .pipe(
          tap((isLoggedIn) => (this.loggedIn = isLoggedIn)),
          tap(() => this.listenToRoutes())
        )
        .subscribe()
    );
    this.screenWidth = window.innerWidth;
    this.detectMobile();
    this.initSubscriptions();
    await this.loadUserData(); // Podés usar await aquí también
    await this.loadDeviceInfo(); // Esperamos para tener isWebPlatform correctamente
  }

  listenToRoutes() {
    this.router.events.pipe(filter((event) => event instanceof NavigationEnd)).subscribe((event: NavigationEnd) => {
      const url = event.urlAfterRedirects || event.url;
      const isPublic = this.publicRoutesPrefixes.some((route) => url.startsWith(route));
      this.hideMenu = isPublic && !this.loggedIn; // 👈 clave
    });
  }

  ngOnDestroy(): void {
    this.subscription.unsubscribe();
  }

  @HostListener('window:resize')
  onResize(): void {
    this.screenWidth = window.innerWidth;
    this.detectMobile();
  }

  private detectMobile(): void {
    this.mobile = this.screenWidth < 1080;
  }

  private initSubscriptions(): void {
    this.subscription.add(
      this.router.events
        .pipe(
          filter((e): e is NavigationEnd => e instanceof NavigationEnd),
          tap((event) => this.toggleMenu(event.url))
        )
        .subscribe()
    );

    this.subscription.add(
      this.authService.isLoggedIn
        .pipe(
          filter((loggedIn) => loggedIn),
          tap(() => this.logDeviceInfo()),
          tap(() => {
            this.loadingMenu = true;
            this.menuService.buildMenu().then((items) => {
              this.menuItems = items;
              this.loadingMenu = false;
            });
          })
        )
        .subscribe()
    );

    this.subscription.add(
      this.currentCountryService
        .getObservable()
        .pipe(tap((country) => (this.country = country)))
        .subscribe()
    );
  }

  private async loadUserData(): Promise<void> {
    try {
      this.userData = await this.userDataService.refreshUserInfo();
      this.isSubscriber = !!this.userData?.servicio_contable;
    } catch (error) {
      console.error('Error al obtener datos del usuario:', error);
      this.userData = null;
      this.isSubscriber = false;
    }
  }

  private async loadDeviceInfo(): Promise<void> {
    const info = await Device.getInfo();
    this.osInfo = info;
    this.isWebPlatform = info.platform === 'web';
  }
  private async logDeviceInfo(): Promise<void> {
    this.osInfo = await Device.getInfo();
  }

  private toggleMenu(url: string): void {
    const showMenu = !this.rutasSinMenu.includes(url);
    this.menuCtrl.enable(showMenu, 'mainMenu');
    this.menuVisible = showMenu;
  }

  goToHome(): void {
    this.openPopoverId = null;
    this.menuCtrl.close('custom');
    this.navigationService.navigateRoot('/home');
  }

  async shareApp(): Promise<void> {
    if (this.isMobile()) {
      this.menuCtrl.close();
    } else {
      window.open('http://www.tributosimple.com/argentina/compartir-descargar-app', '_system');
    }
  }

  downloadApp(): void {
    const os = this.osInfo?.operatingSystem?.toLowerCase() || '';
    const isIOS = os.includes('ios') || os.includes('mac');

    window.location.href = isIOS
      ? 'https://apps.apple.com/ar/app/tributo-simple-monotributo/id1571573160'
      : 'https://play.google.com/store/apps/details?id=tributosimple.anfler.com.ar&hl=es_AR';
  }

  goToTerms(): void {
    const url = `https://tributosimple.com/terminos-y-condiciones/`;
    const browser = this.browserService.open(url, '_blank');

    if (browser) {
      browser.on('exit').subscribe(() => {
        this.ngZone.run(() => console.log('Usuario cerró el navegador.'));
      });
    }
  }

  openPopover(id: string, event: Event): void {
    event.stopPropagation();
    this.openPopoverId = id;
  }

  private isMobile(): boolean {
    return /Android|iPhone|iPad|iPod/i.test(navigator.userAgent);
  }
}
